import { Component, inject } from '@angular/core';
import {
  NavigationEnd,
  Router,
  RouterLink,
  RouterOutlet,
  UrlTree,
} from '@angular/router';
import { catchError, filter, of, switchMap } from 'rxjs';
import { NgClass } from '@angular/common';
import { initFlowbite } from 'flowbite';
import { MainFooterComponent } from './layout/main-footer/main-footer.component';
import { MainNavBarComponent } from './layout/main-nav-bar/main-nav-bar.component';
import { LoaderComponent } from './shared/components/loader/loader.component';
import { TranslationService } from './core/services/translation.service';
import { CurrencyService } from './core/services/currency.service';
import { MsalService } from '@azure/msal-angular';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { NetworkErrorComponent } from './shared/components/network-error/network-error.component';
import { PixelService } from 'ngx-multi-pixel';
import { LoadAnalyticsService } from './core/services/load-analytics.service';
import { MetaPixelService } from './core/services/meta-pixel.service';
import { GoogleAnalyticsService } from './core/services/google-analytics.service';
import { environment } from '../environments/environment';
import { AuthService } from './core/services/auth.service';

@Component({
  selector: 'app-root',
  imports: [
    RouterOutlet,
    MainFooterComponent,
    MainNavBarComponent,
    NgClass,
    LoaderComponent,
    NetworkErrorComponent,
    RouterLink,
    TranslateModule,
  ],
  templateUrl: './app.component.html',
  styleUrl: './app.component.css',
})
export class AppComponent {
  private metaPixelService = inject(MetaPixelService);
  private ga = inject(GoogleAnalyticsService);
  private loadAnalyticsService = inject(LoadAnalyticsService);
  private pixel = inject(PixelService);
  private router = inject(Router);
  private translationService = inject(TranslationService);
  private currencyService = inject(CurrencyService);
  private translate = inject(TranslateService);
  private msalService = inject(MsalService);
  private authService = inject(AuthService);

  production = environment.production;
  isHomePage: boolean = false;
  isOverlayed: boolean = false;
  accessToken = '';
  isFirstLoad = true;
  isPaymentPage = false;
  isBusinessPage = false;
  isDark = false;

  constructor() {
    this.router.events
      .pipe(filter((e) => e instanceof NavigationEnd))
      .subscribe((event: NavigationEnd) => {
        this.isPaymentPage =
          event.urlAfterRedirects.includes('payment/checkout');
        const urlTree: UrlTree = this.router.parseUrl(event.urlAfterRedirects);

        const url =
          '/' +
          (urlTree.root.children['primary']?.segments
            .map((s) => s.path)
            .join('/') || '');

        // The partner (business) area has its own header and footer
        this.isBusinessPage = url === '/business' || url.startsWith('/business/');

        this.isHomePage =
          url === '/' ||
          url === '/home' ||
          url.startsWith('/order/details') ||
          url === '/manage-account';

        this.isOverlayed =
          url === '/about' ||
          url === '/contact-us' ||
          url === '/privacy' ||
          url === '/terms-conditions' ||
          url === '/faqs' ||
          url === '/terms-of-use' ||
          url === '/feedback' ||
          url === '/manage-account/find-bookings';
      });

    // the first language is loaded before the app starts (app.config.ts), so it sends no change event: set its direction now
    document.documentElement.dir = this.translate.currentLang === 'ar' ? 'rtl' : 'ltr';
    this.translate.onLangChange.subscribe((event) => {
      document.documentElement.dir = event.lang === 'ar' ? 'rtl' : 'ltr';
    });
  }

  ngOnInit() {
    this.initializeMsal();

    this.isPaymentPage = this.router.url.includes('/payment/checkout/');
    this.updateQueryParams();
    if (this.production) {
      try {
        // Load Google Analytics
        this.loadAnalyticsService.loadAnalytics();
        // Meta Pixel initialization
        this.metaPixelService.captureFbclid();
        // Facebook Pixel initialization
        this.pixel.initialize();
      } catch (error) {
        console.error('Trackers initialization failed:', error);
      }
    }
    this.router.events
      .pipe(filter((e) => e instanceof NavigationEnd))
      .subscribe((event: NavigationEnd) => {
        if (this.production) {
          try {
            this.ga.trackPageView(event.urlAfterRedirects);
            this.metaPixelService.trackPageView();
          } catch (error) {
            console.error('Trackers initialization failed:', error);
          }
        }
      });
  }

  goBack() {
    window.history.back();
  }

  ngAfterViewInit(): void {
    initFlowbite();
  }

  private updateQueryParams() {
    this.router.events
      .pipe(filter((event) => event instanceof NavigationEnd))
      .subscribe((event: NavigationEnd) => {
        const tree = this.router.parseUrl(event.urlAfterRedirects);

        const lang =
          tree.queryParams['lang'] ?? this.translationService.uiLang();
        const currency =
          tree.queryParams['currency'] ??
          this.currencyService.currentCurrency();

        this.translationService.setUiLang(lang);
        this.currencyService.setCurrency(currency);

        const needUpdate =
          !tree.queryParams['lang'] || !tree.queryParams['currency'];

        if (needUpdate) {
          this.router.navigate([], {
            queryParams: { lang, currency },
            queryParamsHandling: 'merge',
            replaceUrl: true,
          });
        }
      });
  }

  initializeMsal() {
    this.msalService
      .initialize()
      .pipe(switchMap(() => this.authService.handleRedirect()))
      .subscribe({
        next: (signIn) => {
          // A partner back from the B2C redirect (phones) goes to the partner area
          if (signIn === 'partner') {
            this.authService.completePartnerLogin();
            return;
          }
          if (signIn !== 'regular') return;
          // A regular sign-in from the redirect: business roles go to the partner area
          this.authService
            .loadUser()
            .pipe(catchError(() => of(null)))
            .subscribe((user) => this.authService.completeSignIn(user));
        },
        error: () => {},
      });
  }
}
